import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DocumentTextIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { legalDisclaimerApi } from "../api/cmsApi";
import toast from "react-hot-toast";

export default function Legal() {
  const queryClient = useQueryClient();
  const [content, setContent] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["legal-disclaimer"],
    queryFn: () => legalDisclaimerApi.get().then((res) => res.data.data),
  });

  useEffect(() => {
    if (data?.content) {
      setContent(data.content);
      setHasChanges(false);
    }
  }, [data]);

  const updateMutation = useMutation({
    mutationFn: (newContent: string) => legalDisclaimerApi.update(newContent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["legal-disclaimer"] });
      setHasChanges(false);
      toast.success("Rechtlicher Hinweis gespeichert");
    },
    onError: () => toast.error("Fehler beim Speichern"),
  });

  const initMutation = useMutation({
    mutationFn: () => legalDisclaimerApi.init(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["legal-disclaimer"] });
      toast.success("Standardtext geladen");
    },
    onError: () => toast.error("Fehler beim Initialisieren"),
  });

  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    setHasChanges(newContent !== data?.content);
  };

  const handleSave = () => {
    updateMutation.mutate(content);
  };

  const handleReset = () => {
    if (data?.content) {
      setContent(data.content);
      setHasChanges(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-3">
          <DocumentTextIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Rechtliche Hinweise
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Verwalte den rechtlichen Hinweistext, der auf allen Seiten angezeigt wird
            </p>
          </div>
        </div>
        {!data && (
          <button
            onClick={() => initMutation.mutate()}
            disabled={initMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            <ArrowPathIcon
              className={`h-4 w-4 mr-2 ${initMutation.isPending ? "animate-spin" : ""}`}
            />
            {initMutation.isPending ? "Initialisiere..." : "Standardtext laden"}
          </button>
        )}
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 flex gap-3">
        <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
            Globaler Hinweistext
          </h3>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Dieser Text wird auf <strong>allen Seiten</strong> nach dem Footer angezeigt und kann nicht
            deaktiviert werden. Er dient zur rechtlichen Absicherung und Information der Nutzer über die
            Geschäftsbeziehung mit FourWorld Capital.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">Laden...</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="p-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Hinweistext
              </label>
              <textarea
                value={content}
                onChange={(e) => handleContentChange(e.target.value)}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 min-h-[200px]"
                placeholder="Rechtlicher Hinweistext..."
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {content.length} Zeichen
              </p>
            </div>

            {hasChanges && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 flex items-center gap-2">
                <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span className="text-sm text-amber-700 dark:text-amber-300">
                  Ungespeicherte Änderungen vorhanden
                </span>
              </div>
            )}

            <div className="flex gap-2 justify-end">
              {hasChanges && (
                <button
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Zurücksetzen
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending || !hasChanges}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 disabled:opacity-50"
              >
                {updateMutation.isPending ? "Speichern..." : "Speichern"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Vorschau</h2>
        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-6">
          <div className="bg-gray-800 dark:bg-gray-900 rounded-lg p-4">
            <p className="text-xs text-gray-400 leading-relaxed">
              {content || "Kein Text vorhanden"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
